// Co-located helpers for the Material Verification (by production) screen.

/**
 * Groups dispensed containers into material cards keyed by
 * inhouse batch number + lot + part. Each card is verified and approved
 * independently so progress can be persisted card-by-card.
 */
export const buildMaterialCards = (weighingMaterials = []) => {
  const grouped = {};
  weighingMaterials.forEach((item) => {
    const key = `${item?.batch_detail?.inhouse_batch_number}|${item?.lot_no}|${item?.part_no}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return Object.entries(grouped).map(([key, containers]) => ({
    key,
    containers
  }));
};

/**
 * Compares an entered gross weight against the dispensed gross weight using the
 * configured tolerance. Returns 'match' | 'within' | 'beyond' | null.
 */
export const getGrossStatus = (
  container,
  enteredWeights = {},
  tolerance = null
) => {
  const expected = parseFloat(container?.gross_weight);
  const entered = parseFloat(enteredWeights[container?.id]);
  if (Number.isNaN(expected) || Number.isNaN(entered)) return null;
  if (expected === entered) return 'match';
  const tolerancePct = parseFloat(tolerance?.tolerance_percentage) || 0;
  if (tolerancePct <= 0) return 'beyond';
  const allowed = (expected * tolerancePct) / 100;
  return Math.abs(entered - expected) <= allowed ? 'within' : 'beyond';
};

export const isWeightMatched = (container, enteredWeights, tolerance) => {
  const status = getGrossStatus(container, enteredWeights, tolerance);
  return status === 'match' || status === 'within';
};

/**
 * Joins a balance name and dispense booth id into the `balanceName-boothId`
 * display label. Falls back to just the name when no booth id is present.
 * Balance name and dispense booth id are stored separately on the weighing
 * row — this label is only for display (dropdown, PDF).
 */
export const combineBalanceLabel = (balanceName, boothId) => {
  const name = balanceName || '';
  const id = boothId || '';
  return id ? `${name}-${id}` : name;
};

/**
 * Display label for a dispense booth record (`balanceName-dispenseBoothId`).
 */
export const formatBalanceName = (booth) =>
  combineBalanceLabel(booth?.balance_name, booth?.dispense_booth_id);

/**
 * Builds the "Product Batch Number" header value. Appends the dispense
 * request's process type and sub process to the batch number so the screen
 * shows the full dispense context, e.g. `BPA126055G - Granulation - Sifting`.
 * Each segment is included only when present, and the batch number falls back
 * to 'NA' when missing.
 */
export const formatProductBatchLabel = (prodDetails = {}) => {
  const batch_number = prodDetails?.product_batch_number || 'NA';
  const process_type =
    prodDetails?.prod_label_process_master?.process_label_name;
  const sub_process = prodDetails?.prod_label_sub_process?.sub_process_name;
  return [batch_number, process_type, sub_process].filter(Boolean).join(' - ');
};
